import React, { useEffect, useRef, useState, useCallback } from 'react';

import type { DrawerProps, DrawerWidth } from './types';

const WIDTH_MAP: Record<DrawerWidth, string> = {
  sm: '320px',
  md: '420px',
  lg: '560px',
  xl: '720px',
};

/**
 * Drawer — overlay lateral com tabs opcionais.
 *
 * Pattern DS v2.2.0 — primitivo universal para contexto lateral.
 * Usado em Brief 5 A1 (6 abas) + B1 (6 abas LGPD) + Brief 2A ContextDrawer.
 *
 * Acessibilidade:
 * - role="dialog" + aria-modal="true" + aria-labelledby
 * - Esc fecha
 * - Focus trap dentro do drawer
 * - Tablist com roving tabindex (tabs mode)
 */
export function Drawer({
  isOpen,
  onClose,
  title,
  side = 'right',
  width = 'md',
  tabs,
  defaultTabId,
  children,
  footer,
}: DrawerProps) {
  const [activeTabId, setActiveTabId] = useState<string>(defaultTabId ?? tabs?.[0]?.id ?? '');
  const drawerRef = useRef<HTMLDivElement>(null);
  const titleId = useRef(`drawer-title-${Math.random().toString(36).slice(2)}`);
  const hasTabs = tabs && tabs.length > 0;

  // Sync defaultTabId when tabs change externally
  useEffect(() => {
    if (defaultTabId) {
      setActiveTabId(defaultTabId);
    } else if (tabs !== undefined) {
      const first = tabs[0];
      if (first !== undefined && !tabs.find((t) => t.id === activeTabId)) {
        setActiveTabId(first.id);
      }
    }
  }, [defaultTabId, tabs, activeTabId]);

  // Esc to close
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    },
    [isOpen, onClose],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Focus first focusable element when opening
  useEffect(() => {
    if (isOpen && drawerRef.current) {
      const focusable = drawerRef.current.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      focusable?.focus();
    }
  }, [isOpen]);

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const widthValue = WIDTH_MAP[width];
  const activeTab = hasTabs ? tabs.find((t) => t.id === activeTabId) : null;

  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    zIndex: 1000,
    display: 'flex',
    justifyContent: side === 'right' ? 'flex-end' : 'flex-start',
  };

  const drawerStyle: React.CSSProperties = {
    position: 'relative',
    width: widthValue,
    maxWidth: '100vw',
    height: '100%',
    backgroundColor: 'var(--bg-surface, #fff)',
    borderLeft: side === 'right' ? '1px solid var(--border-default, #e5e7eb)' : 'none',
    borderRight: side === 'left' ? '1px solid var(--border-default, #e5e7eb)' : 'none',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: 'var(--shadow-lg, 0 20px 60px rgba(0,0,0,0.15))',
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    borderBottom: '1px solid var(--border-default, #e5e7eb)',
    flexShrink: 0,
  };

  const titleRowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 'var(--space-4, 16px)',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: 'var(--text-base, 14px)',
    fontWeight: 'var(--fw-semibold, 600)',
    color: 'var(--fg-1, #111)',
    margin: 0,
  };

  const closeButtonStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    borderRadius: 'var(--radius-sm, 5px)',
    cursor: 'pointer',
    color: 'var(--fg-3, #6b7280)',
    background: 'transparent',
    border: 'none',
    flexShrink: 0,
  };

  const tablistStyle: React.CSSProperties = {
    display: 'flex',
    padding: '0 var(--space-4, 16px)',
    gap: 'var(--space-1, 4px)',
    borderTop: '1px solid var(--border-default, #e5e7eb)',
  };

  const bodyStyle: React.CSSProperties = {
    flex: 1,
    overflowY: 'auto',
    padding: 'var(--space-4, 16px)',
  };

  const footerStyle: React.CSSProperties = {
    borderTop: '1px solid var(--border-default, #e5e7eb)',
    padding: 'var(--space-4, 16px)',
    flexShrink: 0,
  };

  return (
    <div
      style={overlayStyle}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      data-testid="drawer-overlay"
    >
      <div
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId.current : undefined}
        style={drawerStyle}
        data-testid="drawer"
      >
        {/* Header */}
        <div style={headerStyle}>
          <div style={titleRowStyle}>
            {title && (
              <h2 id={titleId.current} style={titleStyle}>
                {title}
              </h2>
            )}
            <button
              type="button"
              onClick={onClose}
              style={closeButtonStyle}
              aria-label="Fechar"
              data-testid="drawer-close"
            >
              ✕
            </button>
          </div>

          {/* Tabs */}
          {hasTabs && (
            <div role="tablist" aria-label={title ?? 'Navegação'} style={tablistStyle}>
              {tabs.map((tab) => {
                const isActive = tab.id === activeTabId;
                return (
                  <button
                    key={tab.id}
                    role="tab"
                    type="button"
                    id={`tab-${tab.id}`}
                    aria-selected={isActive}
                    aria-controls={`tabpanel-${tab.id}`}
                    tabIndex={isActive ? 0 : -1}
                    onClick={() => setActiveTabId(tab.id)}
                    style={{
                      padding: 'var(--space-2, 8px) var(--space-3, 12px)',
                      fontSize: 'var(--text-sm, 12px)',
                      fontWeight: isActive ? 'var(--fw-semibold, 600)' : 'var(--fw-regular, 400)',
                      color: isActive ? 'var(--brand, #d97706)' : 'var(--fg-2, #374151)',
                      background: 'transparent',
                      border: 'none',
                      borderBottom: isActive
                        ? '2px solid var(--brand, #d97706)'
                        : '2px solid transparent',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-1, 4px)',
                      marginBottom: '-1px',
                    }}
                    data-testid={`drawer-tab-${tab.id}`}
                  >
                    {tab.label}
                    {tab.badgeCount !== undefined && tab.badgeCount > 0 && (
                      <span
                        style={{
                          backgroundColor: 'var(--brand, #d97706)',
                          color: 'var(--fg-on-accent, #fff)',
                          borderRadius: 'var(--radius-pill, 999px)',
                          fontSize: 'var(--text-2xs, 10px)',
                          fontWeight: 'var(--fw-semibold, 600)',
                          padding: '1px 5px',
                          lineHeight: 1.4,
                        }}
                      >
                        {tab.badgeCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Body */}
        <div style={bodyStyle}>
          {hasTabs && activeTab ? (
            <div
              role="tabpanel"
              id={`tabpanel-${activeTab.id}`}
              aria-labelledby={`tab-${activeTab.id}`}
              tabIndex={0}
            >
              {activeTab.content}
            </div>
          ) : (
            children
          )}
        </div>

        {/* Footer */}
        {footer && <div style={footerStyle}>{footer}</div>}
      </div>
    </div>
  );
}
